import React, { forwardRef, InputHTMLAttributes, ReactNode, useState } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input label */
  label?: string;
  /** Helper text below input */
  helperText?: string;
  /** Error message (also sets error state) */
  error?: string;
  /** Success state */
  success?: boolean;
  /** Input size */
  size?: 'sm' | 'md' | 'lg';
  /** Icon to display on the left */
  leftIcon?: ReactNode;
  /** Icon to display on the right */
  rightIcon?: ReactNode;
  /** Full width input */
  fullWidth?: boolean;
  /** Input variant style */
  variant?: 'default' | 'filled';
  /** Additional container class */
  containerClassName?: string;
}

const sizeStyles = {
  sm: 'h-9 px-3 text-sm rounded-lg',
  md: 'h-11 px-4 text-base rounded-xl',
  lg: 'h-13 px-5 text-lg rounded-xl',
};

const iconSizeStyles = {
  sm: 'pl-9',
  md: 'pl-11',
  lg: 'pl-12',
};

const rightIconSizeStyles = {
  sm: 'pr-9',
  md: 'pr-11',
  lg: 'pr-12',
};

/**
 * Modern Input component with labels, icons, and validation states
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      success,
      size = 'md',
      leftIcon,
      rightIcon,
      fullWidth = true,
      variant = 'default',
      containerClassName = '',
      className = '',
      type = 'text',
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const baseStyles = `
      w-full font-normal
      bg-white dark:bg-gray-800
      text-gray-900 dark:text-gray-100
      placeholder:text-gray-400 dark:placeholder:text-gray-500
      transition-all duration-150 ease-out
      focus:outline-none
    `;

    const variantStyles = {
      default: `
        border border-gray-200 dark:border-gray-700
        hover:border-gray-300 dark:hover:border-gray-600
        focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10
      `,
      filled: `
        border-transparent
        bg-gray-100 dark:bg-gray-700
        hover:bg-gray-50 dark:hover:bg-gray-600
        focus:bg-white dark:focus:bg-gray-800 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10
      `,
    };

    let stateStyles = '';
    if (error) {
      stateStyles = 'border-red-500 hover:border-red-500 focus:border-red-500 focus:ring-red-500/10';
    } else if (success) {
      stateStyles = 'border-emerald-500 hover:border-emerald-500 focus:border-emerald-500 focus:ring-emerald-500/10';
    }

    const disabledStyles = disabled ?
      'bg-gray-50 dark:bg-gray-900 text-gray-400 cursor-not-allowed opacity-70'
      : '';

    const inputClasses = [
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      leftIcon ? iconSizeStyles[size] : '',
      rightIcon || isPassword ? rightIconSizeStyles[size] : '',
      stateStyles,
      disabledStyles,
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    const containerClasses = [
      'flex flex-col gap-1.5',
      fullWidth ? 'w-full' : '',
      containerClassName,
    ]
      .join(' ')
      .trim();

    const helperTextColorClass = (() => {
      if (error) return 'text-red-500';
      if (success) return 'text-emerald-500';
      return 'text-gray-500 dark:text-gray-400';
    })();

    return (
      <div className={containerClasses}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className='text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            {label}
            {props.required && <span className='text-red-500 ml-1'>*</span>}
          </label>
        )}

        {/* Input wrapper */}
        <div className='relative'>
          {/* Left icon */}
          {leftIcon && (
            <span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'>
              {leftIcon}
            </span>
          )}

          {/* Input element */}
          {(() => {
            let currentType = type;
            if (isPassword && showPassword) {
              currentType = 'text';
            } else if (type === 'submit') {
              currentType = 'submit';
            } else if (type === 'reset') {
              currentType = 'reset';
            }

            const isKnownType = ['submit', 'reset', 'button', 'text', 'email', 'password', 'tel', 'url', 'search', 'number'].includes(currentType);
            const finalType = isKnownType ? currentType : 'text';

            return (
              <input
                ref={ref}
                id={inputId}
                type={finalType}
                disabled={disabled}
                className={inputClasses}
                {...props}
              />
            );
          })()}

          {/* Right icon or password toggle */}
          {(rightIcon || isPassword) && (
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 ${
                isPassword ? 'cursor-pointer hover:text-gray-600' : 'pointer-events-none'
              }`}
              onClick={isPassword ? () => setShowPassword(!showPassword) : undefined}
            >
              {(() => {
                if (!isPassword) return rightIcon;
                return showPassword ? (
                  <AiOutlineEyeInvisible className='w-5 h-5' />
                ) : (
                  <AiOutlineEye className='w-5 h-5' />
                );
              })()}
            </span>
          )}
        </div>

        {/* Helper text or error */}
        {(helperText || error) && (
          <p className={`text-sm ${helperTextColorClass}`}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
